import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Button, Text } from '@mantine/core';
import { useCurrentEditor } from '@tiptap/react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import {
	canMakeSelectionConditional,
	findConditionalAncestor,
	makeSelectionConditional,
	unwrapConditionalBlock,
	unwrapConditionalInline,
} from '../utils/conditionalCommands';
import { defaultConditionVariable } from '../utils/conditionVariables';
import { getVariableInfo } from '../utils/emailVariableCatalog';
import { useTemplateVariables } from '../utils/templateVariablesContext';

const Wrapper = styled.div`
	padding: 1rem;
	border-top: 1px solid var(--mantine-color-gray-4);

	button {
		height: 2rem;
		padding-inline: 0.75rem;
		font-size: 0.75rem;
		line-height: 1rem;
	}
`;

interface ActiveCondition {
	pos: number;
	variable: string;
	kind: 'block' | 'inline';
}

export const ConditionalLogicPanel = () => {
	const tMipManage = useTranslationMipManage();
	const { editor } = useCurrentEditor();
	const templateVariables = useTemplateVariables();
	const [active, setActive] = useState<ActiveCondition | null>(null);
	const [canWrap, setCanWrap] = useState(false);

	useEffect(() => {
		if (!editor) {
			return;
		}
		const read = () => {
			setActive(findConditionalAncestor(editor.state));
			setCanWrap(canMakeSelectionConditional(editor.state));
		};
		editor.on('selectionUpdate', read);
		editor.on('transaction', read);
		read();
		return () => {
			editor.off('selectionUpdate', read);
			editor.off('transaction', read);
		};
	}, [editor]);

	const canMake = canWrap && templateVariables.length > 0;
	if (!editor || (!active && !canMake)) {
		return null;
	}

	return (
		<Wrapper>
			<Text fw={600} fz="0.8125rem" lh="1.21875rem" mb={4}>
				{tMipManage('email_templates.editor.conditional.panel_title', {
					defaultValue: 'Conditional logic',
				})}
			</Text>
			{active ? (
				<>
					<Text fz="0.75rem" lh="1.125rem" c="gray.9" mb="0.75rem">
						{tMipManage('email_templates.editor.conditional.panel_summary', {
							defaultValue: 'Only shown when {{label}} has a value.',
							label: getVariableInfo(active.variable).label.toLowerCase(),
						})}
					</Text>
					<Button
						variant="default"
						fullWidth
						onMouseDown={(event) => event.preventDefault()}
						onClick={() =>
							active.kind === 'inline'
								? unwrapConditionalInline(editor, active.pos)
								: unwrapConditionalBlock(editor, active.pos)
						}
					>
						{tMipManage('email_templates.editor.conditional.remove', {
							defaultValue: 'Remove condition',
						})}
					</Button>
				</>
			) : (
				<>
					<Text fz="0.75rem" lh="1.125rem" c="gray.9" mb="0.75rem">
						{tMipManage('email_templates.editor.conditional.panel_hint', {
							defaultValue:
								'Show the selected content only when a variable has a value.',
						})}
					</Text>
					<Button
						variant="default"
						fullWidth
						onMouseDown={(event) => event.preventDefault()}
						onClick={() =>
							makeSelectionConditional(
								editor,
								defaultConditionVariable(templateVariables)
							)
						}
					>
						{tMipManage('email_templates.editor.conditional.make', {
							defaultValue: 'Make conditional',
						})}
					</Button>
				</>
			)}
		</Wrapper>
	);
};
